// The two replay endpoints (server/replay/api.ts), from the page's own origin — the deployed server serves
// the page, and scripts/dev.ts forwards /api/replays to the game server. Every failure comes back as a
// ReplayError code (worded by replayErrors.ts) for the screen that asked, never as a throw.
import type { ReplayFile, ReplaySummary } from '../../shared/replay/format.js';
import { parseReplayText, readText } from './parseReplayText.js';
import { PROTOCOL_VERSION } from '../../shared/protocol.js';
import { MAP_HASH } from '../../core/map/active.js';
import type { ReplayError } from './replayErrors.js';

const API = '/api/replays';

export const REPLAY_ERRORS = {
  notFound: 'notFound',
  /** Straight after a match: the server named a replay but the file never appeared (its write failed). */
  unavailable: 'unavailable',
  network: 'network',
  busy: 'busy',
  corrupt: 'corrupt',
  list: 'list',
  /** Replays are not kept across game changes: only one recorded by this protocol and map plays. */
  outdated: 'outdated',
} as const satisfies Record<ReplayError, ReplayError>;

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;
const wait = (ms: number) => new Promise(r => setTimeout(r, ms));

export type ListResult = { ok: true; replays: ReplaySummary[] } | { ok: false; error: ReplayError };

/** The player's own recent matches, newest first. */
export async function fetchReplayList(playerKey: string, limit = 30, fetchFn: Fetch = fetch): Promise<ListResult> {
  try {
    const res = await fetchFn(`${API}?limit=${limit}`, { headers: { Authorization: `Bearer ${playerKey}` } });
    if (!res.ok) return { ok: false, error: res.status === 429 ? REPLAY_ERRORS.busy : REPLAY_ERRORS.list };
    const body = await res.json() as unknown;
    const list = (body as { replays?: unknown }).replays;
    if (!Array.isArray(list)) return { ok: false, error: REPLAY_ERRORS.list };
    return { ok: true, replays: list.filter(isSummary) };
  } catch {
    return { ok: false, error: REPLAY_ERRORS.network };
  }
}

const isSummary = (s: unknown): s is ReplaySummary => {
  const o = s as ReplaySummary;
  return typeof o === 'object' && o !== null && typeof o.id === 'string' && typeof o.startedAt === 'string'
    && typeof o.durationSec === 'number' && Array.isArray(o.heroes) && typeof o.score === 'object' && o.score !== null
    && o.heroes.every(h => typeof h === 'object' && h !== null && typeof h.kind === 'string' && (h.team === 'a' || h.team === 'b')
      && (h.name === null || typeof h.name === 'string'));
};

export type LoadResult = { ok: true; file: ReplayFile } | { ok: false; error: ReplayError };

export type LoadOptions = {
  /** Straight after a match the server may still be writing the file: a 404 is retried this many times. */
  notFoundRetries?: number;
  retryDelayMs?: number;
  fetchFn?: Fetch;
};

export async function loadReplay(id: string, opts: LoadOptions = {}): Promise<LoadResult> {
  const { notFoundRetries = 0, retryDelayMs = 1500, fetchFn = fetch } = opts;
  let res: Response;
  for (let attempt = 0; ; attempt++) {
    try {
      res = await fetchFn(`${API}/${encodeURIComponent(id)}`);
    } catch {
      return { ok: false, error: REPLAY_ERRORS.network };
    }
    if (res.status !== 404 || attempt >= notFoundRetries) break;
    await wait(retryDelayMs);
  }
  if (res.status === 404 || res.status === 400) return { ok: false, error: notFoundRetries > 0 ? REPLAY_ERRORS.unavailable : REPLAY_ERRORS.notFound };
  if (res.status === 429) return { ok: false, error: REPLAY_ERRORS.busy };
  if (!res.ok) return { ok: false, error: REPLAY_ERRORS.network };
  let text: string;
  try {
    text = await readText(res);
  } catch {
    return { ok: false, error: REPLAY_ERRORS.network };
  }
  const file = await parseReplayText(text);
  if (!file) return { ok: false, error: REPLAY_ERRORS.corrupt };
  if (file.header.protocolVersion !== PROTOCOL_VERSION || file.header.mapHash !== MAP_HASH) return { ok: false, error: REPLAY_ERRORS.outdated };
  return { ok: true, file };
}
